import { Link } from "@tanstack/react-router";
import { formatBreakEven, formatCount, formatMoney, formatPercent } from "@shared/roi/format";
import type { CompareEntry } from "@shared/schemas/scenario";
import { Badge } from "@client/components/Badge";

/**
 * The KPI matrix: one column per scenario, one row per figure.
 *
 * Scenarios are columns because that is the shape people already have in their
 * heads — "the aggressive one and the cautious one, side by side" — and because
 * two to four columns fit where two to four blocks of tiles would not.
 *
 * The best figure in a row is marked, but only for the rows where "best" is a
 * fact rather than an opinion: a higher ROI is better, a shorter payback is
 * better, and a bigger net is better. A *lower cost* is not better — it usually
 * means a smaller plan — so the cost row is left unmarked rather than crowning
 * whichever scenario does the least.
 */

type Direction = "higher" | "lower" | "none";

interface Row {
  label: string;
  hint?: string;
  better: Direction;
  /** The comparable number, or null when the scenario has no answer. */
  value: (entry: CompareEntry) => number | null;
  render: (entry: CompareEntry) => string;
  tone?: (entry: CompareEntry) => string | undefined;
}

const ROWS: Row[] = [
  {
    label: "ROI",
    hint: "Net over cost, across the whole horizon.",
    better: "higher",
    value: (entry) => entry.summary.roiPct,
    render: (entry) => formatPercent(entry.summary.roiPct),
  },
  {
    label: "Break-even",
    hint: "When cumulative revenue overtakes cumulative cost.",
    better: "lower",
    // Never breaking even sorts worst, not best — see `bestIndex`.
    value: (entry) => entry.summary.breakEvenYears,
    render: (entry) => formatBreakEven(entry.summary.breakEvenYears, entry.horizonYears),
  },
  {
    label: "Revenue",
    better: "none",
    value: (entry) => entry.summary.revenue,
    render: (entry) => formatMoney(entry.currencyCode, entry.summary.revenue, { compact: true }),
    tone: () => "var(--roi-revenue)",
  },
  {
    label: "Cost",
    better: "none",
    value: (entry) => entry.summary.cost,
    render: (entry) => formatMoney(entry.currencyCode, entry.summary.cost, { compact: true }),
    tone: () => "var(--roi-cost)",
  },
  {
    label: "Net",
    better: "higher",
    value: (entry) => entry.summary.net,
    render: (entry) =>
      formatMoney(entry.currencyCode, entry.summary.net, { compact: true, signed: true }),
    tone: (entry) => (entry.summary.net >= 0 ? "var(--roi-revenue)" : "var(--roi-cost)"),
  },
  {
    label: "Ending ARR",
    hint: "Recurring and maintenance revenue in the final year.",
    better: "higher",
    value: (entry) => entry.summary.endingArr,
    render: (entry) => formatMoney(entry.currencyCode, entry.summary.endingArr, { compact: true }),
  },
  {
    label: "Active at the end",
    better: "none",
    value: (entry) => entry.summary.endingActive,
    render: (entry) => formatCount(entry.summary.endingActive),
  },
  {
    label: "Horizon",
    better: "none",
    value: (entry) => entry.horizonYears,
    render: (entry) => `${entry.horizonYears} years`,
  },
  {
    label: "Commitment",
    hint: "How long a client is locked in before they can leave.",
    better: "none",
    value: (entry) => entry.commitmentYears,
    render: (entry) => (entry.commitmentYears === 0 ? "None" : `${entry.commitmentYears} years`),
  },
  {
    label: "Warnings",
    hint: "What the engine flagged about the inputs.",
    better: "none",
    value: (entry) => entry.summary.warnings.length,
    render: (entry) =>
      entry.summary.warnings.length === 0 ? "None" : `${entry.summary.warnings.length} to look at`,
  },
];

/**
 * Which column wins a row, or -1 when the answer is not a fact. A tie has no
 * winner: marking the first of two identical figures would invent a difference.
 */
function bestIndex(row: Row, entries: readonly CompareEntry[], comparable: boolean): number {
  if (row.better === "none" || !comparable) return -1;

  let best = -1;
  let bestValue: number | null = null;
  let tied = false;

  entries.forEach((entry, index) => {
    const value = row.value(entry);
    // "Never, within the horizon" is null, and it is the worst answer there is,
    // not the smallest number.
    if (value === null) return;
    if (bestValue === null || (row.better === "higher" ? value > bestValue : value < bestValue)) {
      best = index;
      bestValue = value;
      tied = false;
      return;
    }
    if (value === bestValue) tied = true;
  });

  return tied ? -1 : best;
}

interface CompareMatrixProps {
  entries: readonly CompareEntry[];
  /** False when the scenarios price in different currencies — see the page. */
  comparable: boolean;
}

export function CompareMatrix({ entries, comparable }: CompareMatrixProps) {
  return (
    <div className="-mx-2 overflow-x-auto px-2">
      <table className="w-full min-w-[40rem] border-collapse text-sm">
        <caption className="sr-only">
          Key figures for each scenario in the comparison, one column per scenario
        </caption>
        <thead className="border-b border-line">
          <tr>
            <th
              scope="col"
              className="px-3 py-2 text-left text-[11px] font-bold uppercase tracking-wider text-ink-soft"
            >
              Figure
            </th>
            {entries.map((entry) => (
              <th key={entry.id} scope="col" className="px-3 py-2 text-right align-bottom">
                <Link
                  to="/products/$slug/scenarios/$scenarioId"
                  params={{ slug: entry.productSlug, scenarioId: entry.id }}
                  className="text-sm font-bold text-azure-deep hover:underline"
                >
                  {entry.name}
                </Link>
                <p className="mt-0.5 text-[11px] font-normal text-ink-soft">
                  {entry.productName} · {entry.author.fullName}
                </p>
                {entry.isBaseline && (
                  <p className="mt-1">
                    <Badge kind="info">Baseline</Badge>
                  </p>
                )}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {ROWS.map((row) => {
            const best = bestIndex(row, entries, comparable);
            return (
              <tr key={row.label} className="border-line/60 border-b last:border-0">
                <th scope="row" className="px-3 py-2 text-left font-normal">
                  <span className="font-semibold text-ink">{row.label}</span>
                  {row.hint && <p className="text-[11px] text-ink-soft">{row.hint}</p>}
                </th>
                {entries.map((entry, index) => (
                  <td
                    key={entry.id}
                    className="tabular whitespace-nowrap px-3 py-2 text-right font-semibold"
                    style={{ color: row.tone?.(entry) }}
                  >
                    {row.render(entry)}
                    {index === best && (
                      // A word, not only a colour: the marker has to survive a
                      // greyscale print and a colour-blind reader.
                      <span className="ml-1.5 text-[10px] font-bold uppercase tracking-wider text-ink-soft">
                        best
                      </span>
                    )}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
